import React from "react";
import PolicyTemplate from "./PolicyTemplate";
import siteConfig from "../config/siteConfig";

const cookiePolicy = {
  eyebrow: "Privacy & digital",
  title: "Cookie policy",
  intro: `This policy explains the cookies and browser storage used by ${siteConfig.brandName}. It reflects the current storefront implementation rather than listing advertising or analytics tools that are not enabled.`,
  updated: "October 19, 2026",
  sections: [
    {
      id: "cookie-technologies",
      title: "Technologies covered",
      paragraphs: [
        "A cookie is a small value a website asks a browser to store and return. Local storage keeps data on a device until it is cleared, while session storage normally lasts only for the current browser session. Similar technologies can support the same kinds of functions.",
        "This storefront currently relies on local and session storage for the functions described below. Its application code does not currently set optional analytics or advertising cookies.",
      ],
    },
    {
      id: "cookie-cart",
      title: "Cart storage",
      paragraphs: [
        "The local-storage key “cartItems” records product identifiers, quantity, and selected size or color so your cart remains available when you move between pages or return on the same browser. It does not store your name, address, payment card details, or product descriptions.",
        "This storage supports the shopping service you request. Clearing it empties the locally saved cart.",
      ],
    },
    {
      id: "cookie-wishlist",
      title: "Wishlist storage",
      paragraphs: [
        "The local-storage key “wishlistItems” records product identifiers that you choose to save. It is a functional preference and remains on the browser until you remove saved items, clear site data, or the browser removes it.",
      ],
    },
    {
      id: "cookie-payment",
      title: "Payment-return storage",
      paragraphs: [
        `The session-storage key “${siteConfig.checkout.lastOrderStorageKey}” temporarily keeps an order number, formatted total, and checkout email so the payment-status page can show a useful confirmation after the external payment redirect. It normally ends when the browser session closes and is not used to store card credentials.`,
      ],
    },
    {
      id: "cookie-providers",
      title: "Payment and other providers",
      paragraphs: [
        "When you continue to a payment provider or another third-party service, that provider may set cookies or similar technologies needed for security, fraud prevention, language, payment processing, or its own legally permitted purposes. Those technologies are controlled by the provider and covered by its notice.",
      ],
    },
    {
      id: "cookie-controls",
      title: "Your controls",
      paragraphs: [
        "Browser settings usually let you view, block, or delete site data. Blocking or clearing storage can empty your cart or wishlist and prevent payment confirmation from displaying correctly. Because no optional analytics or advertising storage is currently enabled by the storefront application, there is no separate optional-cookie switch on this site today.",
        "If optional tracking is added later, we will update this policy and provide any notice, consent, or opt-out control required before it is used.",
      ],
    },
    {
      id: "cookie-contact",
      title: "Updates and contact",
      paragraphs: [
        "We update this page when storage purposes, keys, or third-party integrations change materially. The date above identifies the latest review.",
      ],
      email: true,
      emailSuffix: " with questions about browser storage.",
    },
  ],
  related: [
    { to: "/privacy", label: "Privacy policy" },
    { to: "/privacy-rights", label: "Privacy requests" },
    { to: "/security", label: "Security disclosure" },
  ],
};

function Cookies() {
  return <PolicyTemplate policy={cookiePolicy} />;
}

export default Cookies;
